import { planRow, planMark, planPill, planAsCode, actionsRow } from './ui/model-plan'
import { BrandMark } from './ui/brand-mark'
import { AgentIcon } from './AgentIcon'
import { LocalClaudeRow } from './LocalClaude'
import { authClient } from '../lib/auth'
import { selectLocalAgent, useLocalAgent } from '../lib/localAgent'
import { isDesktopShell } from '../lib/shell'
import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from '../lib/api'
import type { AgentModelCatalog, AgentModelOption, DeviceFlow, ModelAccountKind, ModelAccountStatus } from '../lib/api'
import { posthog } from '../lib/posthog'
import { useStore } from '../lib/store'
import { CodeBlock } from './ui/code-block'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Dot } from './ui/dot'
import { ToggleChip, ToggleChipGroup, ToggleChipItem } from './ui/toggle-chip'
import { Segmented, SegmentedItem } from './ui/segmented'
import { reasoningEffortOptions } from '../../shared/modelMenu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { CheckIcon, ChevronDownIcon } from './ui/icons'

/**
 * "Keep the Doop Agent running on my own subscription."
 *
 * OpenAI issues no redirect URI for a hosted app, so connecting ChatGPT takes
 * one of three shapes, cheapest first:
 *
 *  - Doop on this machine: it holds the loopback port and catches the redirect
 *    itself. Approve in the OpenAI tab, nothing to copy.
 *  - Doop hosted: device code — type a short code at auth.openai.com while the
 *    server polls. Needs "device code authorization" on in ChatGPT settings.
 *  - Fallback: paste the dead redirect page's address back here.
 *
 * The code exchange always happens on the server; no token touches this file.
 */

export function useModelAccount(): {
  account: ModelAccountStatus | null
  refresh: () => void
  set: (next: ModelAccountStatus) => void
} {
  const [account, setAccount] = useState<ModelAccountStatus | null>(null)
  const refresh = useCallback(() => {
    api.modelAccount().then(setAccount, () => {})
  }, [])
  useEffect(refresh, [refresh])
  return { account, refresh, set: setAccount }
}

/**
 * The connected provider's live model list, asked for while `wanted` is true
 * (the picker is open, or the Settings pane is mounted) and again whenever
 * the provider changes or `generation` is bumped: the Settings panel bumps it
 * on every connect, rotation and disconnect, since a rotated key keeps the
 * account's kind and `connectedAt` and would otherwise keep showing the old
 * key's models. Null until it arrives and whenever it belongs to another
 * account than the one on screen, so callers fall back to the curated menu.
 */
export function useAgentModels(
  account: ModelAccountStatus | null,
  wanted: boolean,
  generation = 0,
): AgentModelCatalog | null {
  const kind = account?.connected ? account.kind : undefined
  const [loaded, setLoaded] = useState<{ kind: ModelAccountKind; generation: number; catalog: AgentModelCatalog }>()
  useEffect(() => {
    if (!kind || !wanted) return
    let stale = false
    api.agentModels().then(
      (catalog) => {
        if (!stale) setLoaded({ kind, generation, catalog })
      },
      () => {},
    )
    return () => {
      stale = true
    }
  }, [kind, generation, wanted])
  return loaded && loaded.kind === kind && loaded.generation === generation ? loaded.catalog : null
}

/**
 * `chatgpt_plan_type` arrives as OpenAI's internal slug, and some of them do
 * not survive naive capitalisation — "prolite" is the Pro Lite tier, not
 * "Prolite". Known slugs get their real product name; anything new falls back
 * to title case so an unreleased tier still reads sensibly.
 */
const PLAN_NAMES: Record<string, string> = {
  free: 'Free',
  go: 'Go',
  plus: 'Plus',
  pro: 'Pro',
  prolite: 'Pro Lite',
  pro_lite: 'Pro Lite',
  team: 'Business',
  business: 'Business',
  team_business: 'Business',
  enterprise: 'Enterprise',
  edu: 'Edu',
}

function planName(plan: string): string {
  const slug = plan.trim().toLowerCase()
  return (
    PLAN_NAMES[slug] ??
    slug
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  )
}

const planFlow = 'mt-[14px]'
const maSteps = 'mb-3 ml-[18px] mt-[10px] text-body leading-[1.7] text-ink-soft [&_code]:font-mono [&_code]:text-label'
const maWaiting = 'mt-[10px] flex items-center gap-[9px] text-body text-ink-soft'
const maActions = 'mt-3 flex items-center gap-[10px] max-md:flex-col max-md:items-stretch'
/* the "or paste the redirect instead" style link under a flow */
const maToggle = 'mt-4 block px-0 py-0 text-left text-body font-normal text-ink-faint hover:bg-transparent'
/* the paste-your-redirect field: ink-bordered so it reads as the live step */
const maInput = 'rounded-pill border-ink px-3 py-[10px] font-mono focus:ring-0 md:text-xs'
/* buttons in the responsive action rows centre their label once stacked */
const rowBtn = 'max-md:justify-center'

export function ModelAccountPanel({ onChange }: { onChange?: () => void }) {
  const desktop = isDesktopShell()
  const { account, refresh, set } = useModelAccount()
  /* bumped by `settle`, so a rotated key reloads the catalog it unlocks */
  const [catalogGeneration, setCatalogGeneration] = useState(0)
  const catalog = useAgentModels(account, true, catalogGeneration)
  const { data: session } = authClient.useSession()
  const local = useLocalAgent((s) => s.preference)
  const userId = session?.user.id
  const localModel = local?.model ?? 'default'
  const localEffort = local?.effort
  /* switching to a server provider keeps the local choices for a switch back */
  const selectServer = useCallback(async () => {
    if (userId)
      await selectLocalAgent(userId, {
        enabled: false,
        model: localModel,
        ...(localEffort ? { effort: localEffort } : {}),
      })
  }, [userId, localModel, localEffort])
  const [authUrl, setAuthUrl] = useState('')
  /* true while the server is listening on the loopback callback port for us —
     the user approves in the other tab and this one just flips */
  const [waiting, setWaiting] = useState(false)
  const [device, setDevice] = useState<DeviceFlow | null>(null)
  const [redirect, setRedirect] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [showKey, setShowKey] = useState<false | Exclude<ModelAccountKind, 'chatgpt'>>(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [switchFailed, setSwitchFailed] = useState(false)

  const settle = useCallback(
    (next: ModelAccountStatus) => {
      set(next)
      setCatalogGeneration((generation) => generation + 1)
      setAuthUrl('')
      setWaiting(false)
      setDevice(null)
      setRedirect('')
      setApiKey('')
      setShowKey(false)
      setError('')
      setSwitchFailed(false)
      onChange?.()
      /* every allowance meter and wall on screen re-reads, not just this pane */
      useStore.getState().allowanceChanged()
    },
    [set, onChange],
  )

  const activateChatgpt = useCallback(
    async (next: ModelAccountStatus) => {
      // Authorization is complete even if selecting the provider subsequently fails.
      // Keep the connected account visible so retry never exchanges the OAuth code again.
      settle(next)
      setBusy(true)
      try {
        await selectServer()
      } catch {
        setSwitchFailed(true)
        setError('ChatGPT connected, but switching providers failed. Retry the switch without signing in again.')
      } finally {
        setBusy(false)
      }
    },
    [settle, selectServer],
  )

  /* poll only while a sign-in is actually in flight */
  const pendingDevice = device?.status === 'pending'
  useEffect(() => {
    if (!waiting && !pendingDevice) return
    let cancelled = false
    const id = window.setInterval(() => {
      api
        .modelAccount()
        .then(async (next) => {
          // An existing API-key account is not evidence that OAuth succeeded.
          if (cancelled || !next.connected || next.kind !== 'chatgpt') return
          cancelled = true
          await activateChatgpt(next)
        })
        .catch(() => {})
      /* the device flow can also fail server-side (expired, refused) — that
         status is the only place the user would ever learn why */
      if (pendingDevice) {
        api.deviceAuthStatus().then(
          (flow) => {
            if (!cancelled && 'userCode' in flow && flow.status === 'error') {
              setDevice(null)
              setError(flow.error || 'That sign-in did not complete')
            }
          },
          () => {},
        )
      }
    }, 1500)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [waiting, pendingDevice, activateChatgpt])

  const fail = (e: unknown) => {
    const body = (e as { body?: { error?: string } })?.body
    setError(body?.error || (e instanceof Error ? e.message : 'That did not work — try again'))
  }

  const start = async () => {
    setBusy(true)
    setError('')
    try {
      const { url, catching } = await api.chatgptAuthorize()
      if (catching) {
        /* we hold the loopback port, so the browser round trip completes by
           itself — the cheapest flow, and it needs no account setting */
        setAuthUrl(url)
        setWaiting(true)
        window.open(url, '_blank', 'noopener')
        posthog.capture('chatgpt_connect_started', { flow: 'loopback' })
        return
      }
      setDevice(await api.startDeviceAuth())
      posthog.capture('chatgpt_connect_started', { flow: 'device' })
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }

  const cancelDevice = () => {
    setDevice(null)
    api.cancelDeviceAuth().catch(() => {})
  }

  /* the escape hatch when device codes are unavailable (workspace admin has
     not allowed them): fall back to the browser redirect and a paste */
  const usePasteFlow = async () => {
    setBusy(true)
    setError('')
    try {
      const { url } = await api.chatgptAuthorize()
      cancelDevice()
      setAuthUrl(url)
      setWaiting(false)
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }

  const finish = async () => {
    setBusy(true)
    setError('')
    try {
      const next = await api.connectChatgpt(redirect)
      await activateChatgpt(next)
      posthog.capture('chatgpt_connected')
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }

  const saveKey = async () => {
    setBusy(true)
    setError('')
    try {
      const connect =
        showKey === 'anthropic-key'
          ? api.connectAnthropicKey
          : showKey === 'openrouter-key'
            ? api.connectOpenRouterKey
            : showKey === 'gemini-key'
              ? api.connectGeminiKey
              : api.connectOpenAiKey
      const next = await connect(apiKey)
      if (account?.kind !== showKey) await selectServer()
      settle(next)
      posthog.capture('model_account_connected', { kind: showKey || 'openai-key' })
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }

  const pickModel = async (model: string) => {
    setBusy(true)
    setError('')
    try {
      set(await api.setAgentModel(model))
      posthog.capture('agent_model_changed', { model })
    } catch (e) {
      fail(e)
      refresh()
    } finally {
      setBusy(false)
    }
  }

  const pickEffort = async (effort: string) => {
    setBusy(true)
    setError('')
    try {
      set(await api.setAgentEffort(effort))
      posthog.capture('agent_effort_changed', { effort })
    } catch (e) {
      fail(e)
      refresh()
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    try {
      settle(await api.disconnectModelAccount())
      posthog.capture('model_account_disconnected')
    } catch (e) {
      fail(e)
      refresh()
    } finally {
      setBusy(false)
    }
  }

  if (!account) return null

  /* the connected row offers the provider's live list once it has arrived;
     every other row advertises its curated menu */
  const menuFor = (kind: ModelAccountKind): AgentModelOption[] =>
    (account.connected && kind === account.kind && catalog?.live ? catalog.models : undefined) ??
    account.menus?.[kind] ??
    []
  const activeMenu = account.kind ? menuFor(account.kind) : []
  const chosen = activeMenu.find((m) => m.id === account.model)
  const onChatgpt = account.connected && account.kind === 'chatgpt'
  /* only one account is stored per user, so connecting one replaces the other */
  const replaces = account.connected

  /* OpenAI models think as hard as asked: a one-of-four switch under the
     connected OpenAI row, absent elsewhere (the status carries no effort). */
  const effortPicker = () =>
    account.effort ? (
      <div className="mt-[10px] flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-body text-ink-soft">Reasoning effort</span>
        <Segmented aria-label="Reasoning effort" value={account.effort} onValueChange={pickEffort} disabled={busy}>
          {reasoningEffortOptions(account.effort).map((e) => (
            <SegmentedItem key={e.id} value={e.id} title={e.blurb}>
              {e.name}
            </SegmentedItem>
          ))}
        </Segmented>
      </div>
    ) : null

  /* On the connected row the picker changes the model; on any other row it
     only advertises what that plan can run, so it stays inert. A roster too
     big for a chip row (OpenRouter) becomes a dropdown instead. */
  const modelPicker = (live: boolean, kind: ModelAccountKind) => {
    const models = menuFor(kind)
    if (models.length > 5) {
      if (!live) {
        return (
          <p className="self-center text-body leading-[1.55] text-ink-faint">
            {models
              .slice(0, 6)
              .map((m) => m.name)
              .join(' · ')}
            {models.length > 6 ? ' and more' : ''}
          </p>
        )
      }
      const current = models.find((m) => m.id === account.model)
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              disabled={busy}
              className="gap-[7px] rounded-pill border border-line px-3 text-body font-medium text-ink"
            >
              {current?.name ?? account.model ?? 'Pick a model'}
              {current?.vision === false && <NoVisionBadge />}
              <ChevronDownIcon width={14} height={14} aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {models.map((m) => (
              <DropdownMenuItem key={m.id} onSelect={() => void pickModel(m.id)}>
                <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
                  <span className="flex items-center gap-[7px] text-ink">
                    {m.name}
                    {m.vision === false && <NoVisionBadge />}
                  </span>
                  <span className="text-label leading-[1.45] text-ink-faint">{m.blurb}</span>
                </span>
                {m.id === account.model && <Tick />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
    return live ? (
      <ToggleChipGroup aria-label="Model" value={account.model ?? ''} onValueChange={pickModel} disabled={busy}>
        {models.map((m) => (
          <ToggleChipItem key={m.id} value={m.id} title={m.vision === false ? `${m.blurb} ${VISION_NOTE}` : m.blurb}>
            {m.id === account.model && <Tick />}
            {m.name}
          </ToggleChipItem>
        ))}
        {/* a server override outside the known tiers still has to be visible */}
        {account.model && !models.some((m) => m.id === account.model) && (
          <ToggleChip state="on">{account.model}</ToggleChip>
        )}
      </ToggleChipGroup>
    ) : (
      /* inert on a row that is not the connected one — a capability list, not a control */
      <div className="flex flex-wrap gap-[9px]">
        {models.map((m) => (
          <ToggleChip key={m.id} state="idle">
            {m.name}
          </ToggleChip>
        ))}
      </div>
    )
  }

  /* Every key-connected provider wears the same section: title + pill, lede,
     then either the live picker/rotate row, the key-entry flow, or the inert
     capability row with a Connect button. */
  const keySection = (cfg: {
    kind: Exclude<ModelAccountKind, 'chatgpt'>
    title: string
    lede: string
    mark: ReactNode
    placeholder: string
  }) => {
    const live = account.connected && account.kind === cfg.kind
    return (
      <section className={planRow(live && !local?.enabled)} key={cfg.kind}>
        <span className={planMark(live && !local?.enabled)}>{cfg.mark}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-[10px] max-md:flex-wrap max-md:items-start max-md:gap-x-[9px] max-md:gap-y-[6px]">
            <h3 className="font-display text-heading font-extrabold normal-case tracking-[-0.02em] text-ink max-md:text-heading">
              {cfg.title}
            </h3>
            <span className={planPill(live)}>
              {live && showKey !== cfg.kind ? (local?.enabled ? 'Connected' : 'Active · Connected') : 'Not connected'}
            </span>
          </div>
          <p className="mt-1.5 text-copy leading-[1.55] text-ink-soft max-md:text-copy">{cfg.lede}</p>

          {live && showKey !== cfg.kind ? (
            <>
              <div className={actionsRow}>
                {modelPicker(true, cfg.kind)}
                <div className="flex flex-wrap gap-2 max-md:[&>button]:flex-1">
                  <Button
                    disabled={busy}
                    onClick={() => {
                      setApiKey('')
                      setShowKey(cfg.kind)
                    }}
                  >
                    Rotate key
                  </Button>
                  {local?.enabled && (
                    <Button
                      disabled={busy}
                      onClick={() => {
                        selectServer().catch(fail)
                      }}
                    >
                      Use instead
                    </Button>
                  )}
                  <Button variant="danger" className={rowBtn} onClick={remove} disabled={busy}>
                    Disconnect
                  </Button>
                </div>
              </div>
              {chosen && (chosen.blurb || chosen.vision === false) && (
                <p className="mt-[10px] text-body text-ink-faint">
                  {chosen.blurb}
                  {chosen.vision === false && ` ${VISION_NOTE}`}
                </p>
              )}
              {catalog?.error && <p className="mt-[10px] text-body text-ink-faint">{catalog.error}</p>}
              {effortPicker()}
            </>
          ) : showKey === cfg.kind ? (
            <div className={planFlow}>
              <p className="mb-3 mt-2 text-body leading-[1.55] text-ink-soft">
                The key is stored on the server and never shown again.
              </p>
              <Input
                className={maInput}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={cfg.placeholder}
                type="password"
                autoFocus
                spellCheck={false}
              />
              <div className={maActions}>
                <Button variant="primary" className={rowBtn} onClick={saveKey} disabled={busy || !apiKey.trim()}>
                  {busy ? 'Saving…' : live ? 'Rotate key' : 'Save key'}
                </Button>
                <Button variant="ghost" className={rowBtn} onClick={() => setShowKey(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className={actionsRow}>
              {modelPicker(false, cfg.kind)}
              <Button
                className={rowBtn}
                onClick={() => {
                  setApiKey('')
                  setShowKey(cfg.kind)
                }}
                disabled={busy}
              >
                {replaces ? 'Use instead' : 'Connect'}
              </Button>
            </div>
          )}
        </div>
      </section>
    )
  }

  return (
    <div className="flex flex-col">
      {account.chatgptEnabled !== false && (
        <section className={planRow(onChatgpt && !local?.enabled)}>
          <span className={planMark(onChatgpt && !local?.enabled)}>
            <OpenAiMark />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-[10px] max-md:flex-wrap max-md:items-start max-md:gap-x-[9px] max-md:gap-y-[6px]">
              <h3 className="font-display text-heading font-extrabold normal-case tracking-[-0.02em] text-ink max-md:text-heading">
                Codex Plan
              </h3>
              <span className={planPill(onChatgpt)}>
                {onChatgpt ? (local?.enabled ? 'Connected' : 'Active · Connected') : 'Not connected'}
              </span>
            </div>
            <p className="mt-1.5 text-copy leading-[1.55] text-ink-soft max-md:text-copy">
              Route OpenAI models through your ChatGPT subscription
            </p>
            {onChatgpt && account.email && (
              <dl className="mt-[14px] grid grid-cols-[auto_auto] items-center justify-start gap-x-[14px] gap-y-2 text-body text-ink-soft max-md:grid-cols-1 max-md:gap-[3px]">
                <dt>Connected as</dt>
                <dd className="min-w-0">
                  <code className={planAsCode}>{account.email}</code>
                </dd>
                {account.plan && (
                  <>
                    <dt className="max-md:mt-2">Plan</dt>
                    <dd className="min-w-0">
                      <code className={planAsCode}>{planName(account.plan)}</code>
                    </dd>
                  </>
                )}
              </dl>
            )}

            {onChatgpt ? (
              <>
                <div className={actionsRow}>
                  {modelPicker(true, 'chatgpt')}
                  <div className="flex flex-wrap gap-2 max-md:[&>button]:flex-1">
                    {(local?.enabled || switchFailed) && (
                      <Button disabled={busy} onClick={() => activateChatgpt(account)}>
                        {switchFailed ? 'Retry switch' : 'Use instead'}
                      </Button>
                    )}
                    <Button variant="danger" className={rowBtn} onClick={remove} disabled={busy}>
                      Disconnect
                    </Button>
                  </div>
                </div>
                {chosen && <p className="mt-[10px] text-body text-ink-faint">{chosen.blurb}</p>}
                {effortPicker()}
              </>
            ) : device ? (
              <div className={planFlow}>
                <ol className={maSteps}>
                  <li>
                    One-time setup: turn on <b>device code authorization</b> in ChatGPT → Settings → Security. (On a
                    workspace account an admin has to allow it.)
                  </li>
                  <li>
                    Open{' '}
                    <a href={device.verificationUrl} target="_blank" rel="noreferrer noopener">
                      {device.verificationUrl.replace(/^https:\/\//, '')}
                    </a>{' '}
                    and enter this code:
                  </li>
                </ol>
                <CodeBlock text={device.userCode} />
                <p className={maWaiting}>
                  <Dot className="animate-[arrival-pulse_1.6s_ease-in-out_infinite] bg-brand" /> Waiting for approval…
                </p>
                <div className={maActions}>
                  <Button asChild variant="primary" className={rowBtn}>
                    <a href={device.verificationUrl} target="_blank" rel="noreferrer noopener">
                      Open verification page
                    </a>
                  </Button>
                  <Button variant="ghost" className={rowBtn} onClick={cancelDevice}>
                    Cancel
                  </Button>
                </div>
                <Button variant="bare" size="sm" className={maToggle} onClick={usePasteFlow} disabled={busy}>
                  Device codes not available on your account? Use the browser sign-in instead
                </Button>
              </div>
            ) : waiting ? (
              <div className={planFlow}>
                {/* Doop holds the loopback port itself, so approving is the
                    whole job — this row flips on its own. */}
                <p className={maWaiting}>
                  <Dot className="animate-[arrival-pulse_1.6s_ease-in-out_infinite] bg-brand" /> Waiting for you to
                  approve it in the other tab…
                </p>
                <div className={maActions}>
                  <Button asChild variant="ghost" className={rowBtn}>
                    <a href={authUrl} target="_blank" rel="noreferrer noopener">
                      Reopen sign-in
                    </a>
                  </Button>
                  <Button
                    variant="bare"
                    size="sm"
                    className={cn(maToggle, 'mt-0 underline')}
                    onClick={() => setWaiting(false)}
                  >
                    Paste the redirect URL instead
                  </Button>
                </div>
              </div>
            ) : authUrl ? (
              <div className={planFlow}>
                <ol className={maSteps}>
                  <li>Approve the connection in the tab that just opened.</li>
                  <li>
                    You will land on a <code>localhost:1455</code> page that <b>fails to load</b> — that is expected.
                  </li>
                  <li>Copy that page's full address and paste it below.</li>
                </ol>
                <Input
                  className={maInput}
                  value={redirect}
                  onChange={(e) => setRedirect(e.target.value)}
                  placeholder="http://localhost:1455/auth/callback?code=…"
                  autoFocus
                  spellCheck={false}
                />
                <div className={maActions}>
                  <Button variant="primary" className={rowBtn} onClick={finish} disabled={busy || !redirect.trim()}>
                    {busy ? 'Connecting…' : 'Finish connecting'}
                  </Button>
                  <Button asChild variant="ghost" className={rowBtn}>
                    <a href={authUrl} target="_blank" rel="noreferrer noopener">
                      Reopen sign-in
                    </a>
                  </Button>
                </div>
              </div>
            ) : (
              <div className={actionsRow}>
                {modelPicker(false, 'chatgpt')}
                <Button className={rowBtn} onClick={start} disabled={busy}>
                  {busy ? 'Opening…' : replaces ? 'Use instead' : 'Connect'}
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {keySection({
        kind: 'openai-key',
        title: 'OpenAI API key',
        lede: 'Pay per token on your own OpenAI account — no ChatGPT subscription involved',
        mark: <OpenAiMark />,
        placeholder: 'sk-…',
      })}

      {keySection({
        kind: 'openrouter-key',
        title: 'OpenRouter API key',
        lede: 'One key for Kimi, Qwen, GLM, DeepSeek, MiniMax, Gemini and more — pay per token on your OpenRouter account',
        mark: <OpenRouterMark />,
        placeholder: 'sk-or-…',
      })}

      {keySection({
        kind: 'gemini-key',
        title: 'Gemini API key',
        lede: 'Google’s Gemini models, plus Nano Banana image generation — pay per token on your Google AI account',
        mark: <GeminiMark />,
        placeholder: 'AIza…',
      })}

      {desktop && <LocalClaudeRow />}
      {keySection({
        kind: 'anthropic-key',
        title: 'Claude API key',
        lede: 'Pay per token on your Anthropic account. Runs on Doop’s server.',
        mark: <AgentIcon name="claude" size={20} />,
        placeholder: 'sk-ant-…',
      })}

      {/* the browser cannot run the local CLI, so the plan closes the list as a pointer to the desktop app */}
      {!desktop && <LocalClaudeRow />}
      {error && <p className="mt-[10px] text-body text-accent-ink">{error}</p>}
    </div>
  )
}

const VISION_NOTE = 'Text-only: this model can’t see screenshots, so designs aren’t visually reviewed.'

function Tick() {
  return <CheckIcon width={13} height={13} strokeWidth={2.5} color="var(--success-ink)" aria-hidden />
}

function NoVisionBadge() {
  return (
    <span className="rounded-[5px] bg-paper-deep px-[6px] py-[1px] text-micro font-medium uppercase tracking-[0.04em] text-ink-faint">
      no visual review
    </span>
  )
}

/** A forked-route glyph for OpenRouter, inlined like the other marks. */
function OpenRouterMark() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12h3.5c3 0 4.5-4.5 7.5-4.5h3" />
      <path d="M2 12h3.5c3 0 4.5 4.5 7.5 4.5h3" />
      <path d="M15 4.5l4 3-4 3" />
      <path d="M15 13.5l4 3-4 3" />
    </svg>
  )
}

/* 18 in a 36px tile — the same mark-to-tile ratio the presence avatars use on
   a canvas, so the brand tile reads identically wherever it appears */
function GeminiMark() {
  return <BrandMark brand="gemini" size={18} />
}

function OpenAiMark() {
  return <BrandMark brand="openai" size={18} />
}
